import { useState, useEffect, useRef, useCallback } from 'react';
import { Analysis } from '../types';
import { ShareService } from '../services/shareService';
import { i18n } from '../i18n';
import AnalysisResult from './AnalysisResult';
import { Loader2 } from 'lucide-react';
import Disclaimer from './Disclaimer';

interface SharedAnalysisProps {
  shareId: string;
  onBack: () => void;
  onUseImprovedTemplate?: (template: string, expert?: string, person?: string, customExpert?: string, customPerson?: string) => void;
  theme?: 'dark' | 'light';
  language?: 'ru' | 'en';
}

export default function SharedAnalysis({ shareId, onBack, onUseImprovedTemplate, theme = 'light', language = 'ru' }: SharedAnalysisProps) {
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [originalAnalysis, setOriginalAnalysis] = useState<Analysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOriginal, setIsLoadingOriginal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [isOriginalModified, setIsOriginalModified] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const loadingRef = useRef(false);

  const loadAnalysis = useCallback(async () => {
    // Предотвращаем двойные запросы
    if (loadingRef.current) {
      return;
    }
    
    loadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const data = await ShareService.getSharedAnalysis(shareId);
      
      // Проверяем, что data существует
      if (!data) {
        throw new Error('Получены пустые данные от сервера');
      }
      
      // Используем originalDecision из сервера (сервер всегда возвращает его из исходного анализа)
      // Если по какой-то причине его нет, вычисляем на основе исходных items (fallback для старых анализов)
      const { calculateVerdict } = await import('../utils/verdictCalculator');
      let originalDecision = data.originalDecision;
      
      // Fallback: если originalDecision отсутствует, вычисляем на основе исходных items
      // (для старых анализов, созданных до добавления originalDecision)
      if (!originalDecision && data.items && Array.isArray(data.items) && data.items.length > 0) {
        try {
          // В расшаренном анализе items могут быть изменены, поэтому fallback не идеален
          // Но для старых анализов это лучше, чем ничего
          originalDecision = calculateVerdict(data.items.map((item: any) => ({
            id: item.id,
            text: item.text || '',
            rating: item.rating || 0,
            category: item.category || 'Общее'
          })));
        } catch (err) {
          console.error('Ошибка при расчете вердикта:', err);
          // Если не удалось рассчитать вердикт, продолжаем без него
        }
      }
      
      // Проверяем обязательные поля
      if (!data.items || !Array.isArray(data.items)) {
        throw new Error('Некорректный формат данных анализа');
      }
      
      // Преобразуем данные из API в формат Analysis
      const analysis: Analysis = {
        id: data.id || '',
        external_id: data.external_id,
        title: data.title || '',
        description: data.description || '',
        url: data.url,
        items: data.items.map((item: any, index: number) => {
          // Добавляем проверки для каждого item
          if (!item || typeof item !== 'object') {
            console.error(`Некорректный item на позиции ${index}:`, item);
            // Возвращаем дефолтный item вместо падения
            return {
              id: `fallback-${index}`,
              text: 'Ошибка загрузки данных',
              rating: 0,
              category: 'Общее',
              isModified: false,
              isExpanded: false,
            };
          }
          
          return {
            id: item.id || `item-${index}`,
            text: item.text || '',
            rating: typeof item.rating === 'number' ? item.rating : 0,
            category: item.category || 'Общее',
            isModified: item.isModified || false,
            isExpanded: item.isExpanded || false,
          };
        }),
        createdAt: data.created_at ? new Date(data.created_at) : new Date(),
        shareCreatedAt: data.share_created_at ? new Date(data.share_created_at) : undefined,
        recommendation: data.recommendation || '',
        summary: data.summary,
        improvementSuggestions: data.improvementSuggestions || data.improvement_suggestions,
        improvedRequest: data.improvedRequest || data.improved_request,
        actionPlan: data.actionPlan || data.action_plan,
        expert: data.expert,
        person: data.person,
        question: data.question,
        noQuestion: data.no_question,
        isModified: data.isModified || false,
        analysisBalance: data.analysisBalance,
        userRating: data.userRating || null,
        originalDecision, // Сохраняем исходный вердикт
        category: data.category || null, // Категория анализа
        analysisMode: data.analysisMode || null,
        argumentSelectionLogic: data.argumentSelectionLogic || null,
        plusListTitle: data.plusListTitle || null,
        minusListTitle: data.minusListTitle || null,
        seo: data.seo || null, // SEO-метаданные
      };
      
      setAnalysis(analysis);
      setIsOriginalModified(Boolean(data.is_original_modified));
    } catch (error) {
      console.error('Ошибка при загрузке shared analysis:', error);
      const errorMessage = error instanceof Error ? error.message : 'Ошибка при загрузке анализа';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [shareId]);

  // Функция для загрузки оригинального анализа
  const loadOriginalAnalysis = useCallback(async () => {
    if (loadingRef.current) {
      return;
    }
    
    loadingRef.current = true;
    setIsLoadingOriginal(true);
    setError(null);

    try {
      const data = await ShareService.getOriginalAnalysis(shareId);
      
      if (!data) {
        throw new Error('Получены пустые данные от сервера');
      }
      
      // Используем ту же логику преобразования, что и для изменённого анализа
      const { calculateVerdict } = await import('../utils/verdictCalculator');
      let originalDecision = data.originalDecision;
      
      if (!originalDecision && data.items && Array.isArray(data.items) && data.items.length > 0) {
        try {
          originalDecision = calculateVerdict(data.items.map((item: any) => ({
            id: item.id,
            text: item.text || '',
            rating: item.rating || 0,
            category: item.category || 'Общее'
          })));
        } catch (err) {
          console.error('Ошибка при расчете вердикта:', err);
        }
      }
      
      if (!data.items || !Array.isArray(data.items)) {
        throw new Error('Некорректный формат данных анализа');
      }
      
      const originalAnalysis: Analysis = {
        id: data.id || '',
        external_id: data.external_id,
        title: data.title || '',
        description: data.description || '',
        url: data.url,
        items: data.items.map((item: any, index: number) => {
          if (!item || typeof item !== 'object') {
            return {
              id: `fallback-${index}`,
              text: 'Ошибка загрузки данных',
              rating: 0,
              category: 'Общее',
              isModified: false,
              isExpanded: false,
            };
          }
          
          return {
            id: item.id || `item-${index}`,
            text: item.text || '',
            rating: typeof item.rating === 'number' ? item.rating : 0,
            category: item.category || 'Общее',
            isModified: false, // Оригинальные элементы не изменены
            isExpanded: item.isExpanded || false,
          };
        }),
        createdAt: data.created_at ? new Date(data.created_at) : new Date(),
        shareCreatedAt: data.share_created_at ? new Date(data.share_created_at) : undefined,
        recommendation: data.recommendation || '',
        summary: data.summary,
        improvementSuggestions: data.improvementSuggestions || data.improvement_suggestions,
        improvedRequest: data.improvedRequest || data.improved_request,
        actionPlan: data.actionPlan || data.action_plan,
        expert: data.expert,
        person: data.person,
        question: data.question,
        noQuestion: data.no_question,
        isModified: false, // Оригинальный анализ не изменён
        analysisBalance: data.analysisBalance,
        userRating: data.userRating || null,
        originalDecision,
        category: data.category || null,
        analysisMode: data.analysisMode || null,
        argumentSelectionLogic: data.argumentSelectionLogic || null,
        plusListTitle: data.plusListTitle || null,
        minusListTitle: data.minusListTitle || null,
        seo: data.seo || null, // SEO-метаданные
      };
      
      setOriginalAnalysis(originalAnalysis);
      return true; // Успешная загрузка
    } catch (error) {
      console.error('Ошибка при загрузке оригинального анализа:', error);
      const errorMessage = error instanceof Error ? error.message : 'Ошибка при загрузке оригинального анализа';
      setError(errorMessage);
      return false; // Ошибка загрузки
    } finally {
      // НЕ сбрасываем isLoadingOriginal здесь - это сделает обработчик после задержки
      loadingRef.current = false;
    }
  }, [shareId]);

  // Обработчик переключения с анимацией
  const handleToggleOriginalWithAnimation = useCallback(async () => {
    // Предотвращаем повторное срабатывание во время анимации
    if (isTransitioning || isLoadingOriginal) {
      return;
    }
    
    const newShowOriginal = !showOriginal;
    
    // Если переключаемся на оригинальный и он ещё не загружен, загружаем его
    if (newShowOriginal && !originalAnalysis) {
      // Загружаем данные (это установит isLoadingOriginal в true)
      await loadOriginalAnalysis();
      
      // Сбрасываем флаг загрузки
      setIsLoadingOriginal(false);
      
      // Только после загрузки меняем состояние и запускаем анимацию
      setShowOriginal(newShowOriginal);
      
      // Запускаем анимацию подсвечивания после того, как контент загружен и отображён
      setIsTransitioning(true);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 1000); // Время анимации всех блоков
    } else {
      // Если переключаемся на изменённый или оригинал уже загружен, сразу меняем состояние и запускаем анимацию
      setShowOriginal(newShowOriginal);
      setIsTransitioning(true);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 1000); // Время анимации всех блоков
    }
  }, [showOriginal, originalAnalysis, loadOriginalAnalysis, isTransitioning, isLoadingOriginal]);

  useEffect(() => {
    loadAnalysis();
  }, [loadAnalysis]);

  // Автоматическая прокрутка в начало страницы при монтировании компонента
  useEffect(() => {
    // Плавная прокрутка в начало страницы
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Устанавливаем title страницы при загрузке анализа (с учётом языка)
  useEffect(() => {
    const siteTitle = i18n.t('common:siteTitle');
    if (analysis) {
      const analysisTitle = analysis.seo?.title || analysis.title || analysis.description || 'Результаты анализа';
      document.title = `${analysisTitle} | ${siteTitle}`;
    } else if (isLoading) {
      document.title = siteTitle;
    }
  }, [analysis, isLoading]);

  if (isLoading) {
    const isDark = theme === 'dark';
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDark 
          ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' 
          : 'bg-gray-50'
      }`}>
        <div className="text-center px-4">
          <Loader2 className={`h-12 w-12 sm:h-16 sm:w-16 animate-spin mx-auto mb-4 sm:mb-6 ${
            isDark ? 'text-blue-400' : 'text-blue-600'
          }`} />
          <p className={`text-base sm:text-lg font-medium ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Загрузка анализа...
          </p>
        </div>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center px-4">
          <div className="mb-4 sm:mb-6">
            <svg className="h-10 w-10 sm:h-12 sm:w-12 text-red-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Анализ не найден</h1>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{error || 'Запрашиваемый анализ не существует или был удален.'}</p>
          <button
            onClick={onBack}
            className="btn-primary mx-auto"
          >
            <span>Вернуться на главную</span>
          </button>
        </div>
      </div>
    );
  }

  // Определяем, какой анализ показывать
  const displayAnalysis = showOriginal && originalAnalysis ? originalAnalysis : analysis;

  return (
    <>
      <AnalysisResult
        analysis={displayAnalysis}
        onItemMove={() => {}} // В совместном режиме изменения не сохраняются
        onRatingChange={() => {}} // В совместном режиме изменения не сохраняются
        onAnalysisUpdate={() => {}} // В совместном режиме изменения не сохраняются
        isReadOnly={true} // Запрещаем редактирование в совместном режиме
        onUseImprovedTemplate={onUseImprovedTemplate}
        // Передаем тему и язык
        theme={theme}
        language={language}
        // Убираем функциональность шеринга и экспорта для расшаренных страниц
        onShare={undefined}
        onExportPDF={undefined}
        isSharing={false}
        isExportingPDF={false}
        shareUrl={null}
        hasBeenShared={false}
        shareError={null}
        // Передаем shareId для кнопки "Пожаловаться"
        shareId={shareId}
        // Передаем пропсы для переключателя оригинального/изменённого анализа
        isOriginalModified={isOriginalModified}
        showOriginal={showOriginal}
        onToggleOriginal={handleToggleOriginalWithAnimation}
        isTransitioning={isTransitioning}
        isLoadingOriginal={isLoadingOriginal}
      />
      
      {/* Disclaimer */}
      <Disclaimer className="mt-8" theme={theme} />
    </>
  );
} 